import { Injectable, NgZone, inject } from '@angular/core';
import { environment } from '../../environments/environment';

interface ElementSelectorInfo {
  selector: string;
  element: HTMLElement;
  parent?: string;
  grandparent?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VibeKanbanCompanionService {
  private readonly ngZone = inject(NgZone);
  private isEnabled = false;
  private isAltPressed = false;
  private hoveredElement: HTMLElement | null = null;
  private overlay: HTMLDivElement | null = null;
  private floatingButton: HTMLDivElement | null = null;

  constructor() {
    // Only enable when explicitly enabled in environment (local dev only)
    if ((environment as any).enableWebCompanion === true) {
      this.initialize();
    }
  }

  private initialize(): void {
    this.isEnabled = true;
    // [Selector Copy Tool] Initialized - Hold Alt + Click to copy selector
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('keydown', (e) => this.handleKeyDown(e));
      document.addEventListener('keyup', (e) => this.handleKeyUp(e));
      document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
      document.addEventListener('click', (e) => this.handleClick(e), true);
    });
    this.createOverlay();
    this.createFloatingButton();
  }

  private createOverlay(): void {
    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      position: fixed;
      pointer-events: none;
      border: 2px solid #007bff;
      background-color: rgba(0, 123, 255, 0.1);
      z-index: 999999;
      display: none;
      box-sizing: border-box;
    `;
    document.body.appendChild(this.overlay);
  }

  private createFloatingButton(): void {
    this.floatingButton = document.createElement('div');
    this.updateButtonContent(false);
    this.floatingButton.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 12px 16px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 999998;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      cursor: help;
      transition: all 0.2s ease;
      pointer-events: auto;
    `;

    // Add hover effect
    this.floatingButton.addEventListener('mouseenter', () => {
      if (this.floatingButton) {
        this.floatingButton.style.transform = 'translateY(-2px)';
        this.floatingButton.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
      }
    });

    this.floatingButton.addEventListener('mouseleave', () => {
      if (this.floatingButton) {
        this.floatingButton.style.transform = 'translateY(0)';
        this.floatingButton.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
      }
    });

    document.body.appendChild(this.floatingButton);
  }

  private updateButtonContent(isActive: boolean, copied = false): void {
    if (!this.floatingButton) {
      return;
    }

    if (copied) {
      this.floatingButton.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      this.floatingButton.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
          </svg>
          <span style="font-size: 12px; font-weight: 500;">Copied to clipboard!</span>
        </div>
      `;
      // Reset after 2 seconds
      setTimeout(() => this.updateButtonContent(false), 2000);
    } else if (isActive) {
      this.floatingButton.style.background = 'linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)';
      this.floatingButton.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
          <span style="font-size: 12px; font-weight: 500;">Click to copy selector</span>
        </div>
      `;
    } else {
      this.floatingButton.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      this.floatingButton.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
          </svg>
          <span style="font-size: 12px; font-weight: 500;">Hold Alt + Click</span>
        </div>
      `;
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (event.altKey && !this.isAltPressed) {
      this.isAltPressed = true;
      document.body.style.cursor = 'copy';
      this.updateButtonContent(true);
    }
  }

  private handleKeyUp(event: KeyboardEvent): void {
    if (!event.altKey && this.isAltPressed) {
      this.isAltPressed = false;
      document.body.style.cursor = '';
      this.hideOverlay();
      this.updateButtonContent(false);
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.isAltPressed) {
      return;
    }

    const target = event.target as HTMLElement;
    if (
      target &&
      target !== this.hoveredElement &&
      target !== this.floatingButton &&
      !this.floatingButton?.contains(target)
    ) {
      this.hoveredElement = target;
      this.showOverlay(target);
    }
  }

  private handleClick(event: MouseEvent): void {
    if (!this.isAltPressed) {
      return;
    }

    const target = event.target as HTMLElement;

    // Don't handle clicks on the floating button itself
    if (target === this.floatingButton || this.floatingButton?.contains(target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const selectorInfo = this.getElementSelector(target);
    this.copyToClipboard(selectorInfo);
  }

  private showOverlay(element: HTMLElement): void {
    if (!this.overlay) {
      return;
    }

    const rect = element.getBoundingClientRect();
    this.overlay.style.display = 'block';
    this.overlay.style.top = `${rect.top}px`;
    this.overlay.style.left = `${rect.left}px`;
    this.overlay.style.width = `${rect.width}px`;
    this.overlay.style.height = `${rect.height}px`;
  }

  private hideOverlay(): void {
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
    this.hoveredElement = null;
  }

  private getElementSelector(element: HTMLElement): ElementSelectorInfo {
    const selector = this.generateSelector(element);
    const parent = element.parentElement ? this.generateSelector(element.parentElement) : undefined;
    const grandparent = element.parentElement?.parentElement
      ? this.generateSelector(element.parentElement.parentElement)
      : undefined;

    return {
      selector,
      element,
      parent,
      grandparent
    };
  }

  private generateSelector(element: HTMLElement): string {
    // Priority: ID > unique class > tag with nth-child

    // Check for ID
    if (element.id) {
      return `#${element.id}`;
    }

    // Check for unique classes
    if (element.className && typeof element.className === 'string') {
      const classes = element.className
        .trim()
        .split(/\s+/)
        .filter((c) => c);
      if (classes.length > 0) {
        // Try to find a unique class combination
        const classSelector = `.${classes.join('.')}`;
        const matches = document.querySelectorAll(classSelector);
        if (matches.length === 1) {
          return classSelector;
        }
        // Use first class with tag name
        return `${element.tagName.toLowerCase()}.${classes[0]}`;
      }
    }

    // Fallback to tag with nth-child
    const parent = element.parentElement;
    if (parent) {
      const children = Array.from(parent.children);
      const index = children.indexOf(element) + 1;
      const parentSelector = parent.tagName.toLowerCase();
      return `${parentSelector} > ${element.tagName.toLowerCase()}:nth-child(${index})`;
    }

    // Last resort: just the tag name
    return element.tagName.toLowerCase();
  }

  private async copyToClipboard(selectorInfo: ElementSelectorInfo): Promise<void> {
    const textToCopy = selectorInfo.grandparent
      ? `${selectorInfo.grandparent} > ${selectorInfo.parent} > ${selectorInfo.selector}`
      : selectorInfo.parent
        ? `${selectorInfo.parent} > ${selectorInfo.selector}`
        : selectorInfo.selector;

    try {
      await navigator.clipboard.writeText(textToCopy);
      // [Selector Copy Tool] Copied to clipboard
      this.updateButtonContent(false, true);
    } catch (err) {
      console.error('[Selector Copy Tool] Failed to copy:', err);
      // Fallback for older browsers
      this.fallbackCopy(textToCopy);
    }
  }

  private fallbackCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
      // [Selector Copy Tool] Copied to clipboard (fallback)
      this.updateButtonContent(false, true);
    } catch (err) {
      console.error('[Selector Copy Tool] Fallback copy failed:', err);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  public destroy(): void {
    if (!this.isEnabled) {
      return;
    }

    if (this.overlay?.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    if (this.floatingButton?.parentNode) {
      this.floatingButton.parentNode.removeChild(this.floatingButton);
    }
  }
}
